import React, { useMemo, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { atom, useAtom } from 'jotai';
import { useDropzone } from 'react-dropzone';
import FileListElement from './file_list_element';
import { createFile, getFileURL, controlDocInfoForPreUpload, controlDocInfoForUploadDone, controlDocInfoForUploadFail } from '../../api/api';

const MAX_FILE_NUM = 10; //受け入れ可能なファイルの最大数
const MULTI_UPLOAD_SIZE = 100 * 1024 * 1024;  // マルチアップロードする切れ目のサイズ

const FILE_CHUNK_SIZE = 100 * 1024 * 1024;   // 分割サイズ
const FILE_MAX_SUM_SIZE_GB = 500; // 全ファイルの合計リミット(GB)
const FILE_MAX_SIZE_GB = 50; // ファイルの合計リミット(GB)
const MAX_FILENAME_LEN = 128;
const MAX_UPLOAD_THREAD = 3;  // 同時実行数
const FILE_UPLOAD_RETRY = 5;  // ファイルのアップロードの再試行回数
const FILE_UPLOAD_RETRY_DURATION = 500;  // ファイルアップロードの再試行をする際の間隔（ｍｓ）再試行するたびに２倍になる
// 外枠
const baseStyle = {
    flex: 1,
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    padding: "20px",
    borderWidth: 2,
    borderRadius: 2,
    borderColor: "#eeeeee",
    borderStyle: "dashed",
    backgroundColor: "#fafafa",
    color: "#bdbdbd",
    outline: "none",
    transition: "border .24s ease-in-out",
};

// フォーカスが当たったときの枠
const focusedStyle = {
    borderColor: "#2196f3",
};

//  受け入れ可能なファイル数のドラッグ
const acceptStyle = {
    borderColor: "#00e676",
};

//  上限を超えたファイル数のドラッグ
const rejectStyle = {
    borderColor: "#ff1744",
};

const fileNamesAtom = atom([]);
const fileNamesStateAtom = atom([]);
const fileNumAtom = atom(0);
const uploadAllDocIdsAtom = atom([]);
const uploadDoneDocIdsAtom = atom([]);
const uploadDoneFileNamesAtom = atom([])

function MainComponent({ handleUploadLoading, handleFinishLoading }) {

    document.title = "添付ファイル アップロード| ファーストコンタクトセンター";
    const navigate = useNavigate();
    const location = useLocation();
    const [fileNames, setFileNames] = useAtom(fileNamesAtom);
    const [fileNamesState, setFileNamesState] = useAtom(fileNamesStateAtom);
    const [fileNum, setFileNum] = useAtom(fileNumAtom);
    const [uploadAllDocIds, setUploadAllDocIds] = useAtom(uploadAllDocIdsAtom);
    const [uploadDoneDocIds, setUploadDoneDocIds] = useAtom(uploadDoneDocIdsAtom);
    const [uploadDoneFileNames, setUploadDoneFileNames] = useAtom(uploadDoneFileNamesAtom);
    const userName = location.state.userName;
    const companyName = location.state.companyName;
    const divisionName = location.state.divisionName;
    const secretInfo = location.state.secretInfo;
    const personalInfo = location.state.personalInfo;
    const caseId = location.state.caseId;
    const contactId = location.state.contactId;
    var prev_state = {
        userName: userName,
        companyName: companyName,
        divisionName: divisionName,
        secretInfo: secretInfo,
        personalInfo: personalInfo,
        caseId: caseId,
        contactId: contactId,
    };
    if (location.state.page === true) {
        initFiles();
        location.state.page = false;
    }
    function initFiles() {
        setFileNames([]);
        setFileNum(0);
        setFileNamesState([]);
        setUploadAllDocIds([]);
        setUploadDoneDocIds([]);
        setUploadDoneFileNames([]);
    }
    async function indicateUploadDone(isDone = true) {
        handleFinishLoading(true);
        var uploadFailDocIds = [];
        uploadAllDocIds.forEach((id) => {
            if (!uploadDoneDocIds.find(el => el === id)) {
                uploadFailDocIds.push(id)
            }
        });
        if (isDone) {
            const ret = await controlDocInfoForUploadDone(caseId, contactId, uploadDoneDocIds, uploadFailDocIds);
            if (!ret) {
                alert("現在サーバーが込み合っているか、または、システムメンテナンス中です。時間を置いて再試行してください。");
                handleFinishLoading(false);
                return false
            }
        } else {
            const ret = await controlDocInfoForUploadFail(caseId, contactId, uploadDoneDocIds, uploadFailDocIds);
            if (!ret) {
                alert("現在サーバーが込み合っているか、または、システムメンテナンス中です。時間を置いて再試行してください。");
                handleFinishLoading(false);
                return false
            }
        }
        handleFinishLoading(false);
        return true;
    }

    // ファイルの分割
    const readDividedFile = async function (fileBlob) {
        var fileChunks = [];
        let index = 0;
        for (let cur = 0; cur < fileBlob.size; cur = cur + FILE_CHUNK_SIZE) {
            let fileChunk = fileBlob.slice(cur, cur + FILE_CHUNK_SIZE, 'application/octet-stream');
            fileChunks.push({
                hash: index++,
                blob: fileChunk
            });
        }
        return fileChunks;
    }

    const upload = async () => {
        if (fileNames.length === 0) {
            if (fileNamesState.length === 0) {
                alert("アップロードファイルが選択されていません。\n" +
                    "アップロード不要である場合、「アップロード中止」ボタンを押下してアップロードを終了して下さい。");
            } else {
                alert("アップロードファイルが選択されていません。\n" +
                    "アップロードが完了している場合、「アップロード終了」ボタンを押下してアップロードを終了して下さい。");
            }
            return;
        }
        if (fileNum + fileNamesState.length > MAX_FILE_NUM) {
            alert(`一度にアップロードできるファイル数は、10ファイルまでです。`);
            return;
        }

        if (fileNum >= 1) {
            let sum = 0;
            for (let i = 0; i <= fileNum - 1; i++) {
                sum = sum + fileNames[i].size;
            }
            if ((sum) > (FILE_MAX_SUM_SIZE_GB * 1024 * 1024 * 1024)) {
                alert(`一度にアップロードできる最大容量は、全ファイルの合計が500GBまでです。`);
                return;
            }
        }
        handleUploadLoading(true);
        let resultControlDocInfoForPre = await controlDocInfoForPreUpload(
            caseId,
            contactId,
            userName,
            companyName,
            divisionName,
            secretInfo,
            personalInfo);
        if (resultControlDocInfoForPre === false) {
            alert("現在サーバーが込み合っているか、または、システムメンテナンス中です。時間を置いて再試行してください。");
            handleUploadLoading(false);
            return;
        }
        var upload_result = new Array(fileNames.length);
        const error_message = [
            'ファイル登録に失敗しました。正しい問い合わせかご確認ください。',
            'アクセスできませんでした。アクセス権を確認してください。',
            'ファイルアップロード中にエラーが発生しました。再度アップロードしてください。',
            'アクセスできませんでした。アクセス権を確認してください。',
            '時間をおいて再度お試しください。',
            'ファイルのアップロードが完了しませんでした。再度アップロードしてください。',
            'システムエラー、再度アップロードしてください。'
        ];
        const msSleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));
        const uploadFileFuncs = async (fileName, index) => new Promise(async (resolve, reject) => {
            try {
                var ret = await createFile(
                    caseId,
                    contactId,
                    fileName.name,
                    secretInfo,
                    personalInfo,
                    fileName.size,
                    userName,
                    companyName,
                    divisionName
                );
                if (ret === false) {
                    throw new Error("0");
                }
                var docId = ret.DOC_ID;
                var sleep_ms = FILE_UPLOAD_RETRY_DURATION;
                var response;
                uploadAllDocIds.push(docId);
                setUploadAllDocIds(uploadAllDocIds);
                if (fileName.size < MULTI_UPLOAD_SIZE) {
                    ret = await getFileURL(docId, caseId, 1);
                    if (ret === false) {
                        throw new Error("1");
                    }
                    var url = ret.URL;
                    const parameter = {
                        method: 'PUT',
                        headers: {
                            'Content-Type': fileName.type
                        },
                        body: fileName
                    };
                    for (let i = 0; i < FILE_UPLOAD_RETRY; i++) {
                        try {
                            response = null;
                            response = await fetch(url, parameter);
                        } catch (e) {
                            // 実行時エラーなのでリトライ対象
                            await msSleep(sleep_ms);
                            sleep_ms = sleep_ms * 2; // 再送のたびに再送間隔は２倍になる
                            continue;
                        }
                        if (response.status === 200) {
                            upload_result[index] = {
                                result: true,
                                file: fileName.name,
                                size: fileName.size,
                                index: index,
                                error_code: "9"
                            };
                            uploadDoneDocIds.push(docId);
                            uploadDoneFileNames.push(fileName)
                            setUploadDoneDocIds(uploadDoneDocIds);
                            setUploadDoneFileNames(uploadDoneFileNames);
                            return resolve(1);
                        } else if (response.status === 401 || response.status === 403) {
                            // アクセスエラーは即時エラー
                            throw new Error("3");
                        } else {
                            await msSleep(sleep_ms);
                            sleep_ms = sleep_ms * 2; // 再送のたびに再送間隔は２倍になる
                        }
                    }
                    throw new Error("2");
                }
                else {
                    // マルチパートの開始
                    ret = await getFileURL(docId, caseId, 2);
                    if (ret === false) {
                        throw new Error("1");
                    }
                    var uploadId = ret.UPLOAD_ID;
                    var partInfo = [];
                    var partData = await readDividedFile(fileName);
                    for (var i = 0; i < partData.length; i++) {
                        // マルチパート用URL取得
                        ret = await getFileURL(docId, caseId, 3, uploadId, i + 1);
                        if (ret === false) {
                            throw new Error("4");
                        }
                        url = ret.URL;
                        const parameter = {
                            method: 'PUT',
                            headers: {
                                'Content-Type': 'multipart/form-data'
                            },
                            body: partData[i].blob
                        };
                        let isUploadDone = false;
                        for (let j = 0; j < FILE_UPLOAD_RETRY; j++) {
                            try {
                                response = null;
                                response = await fetch(url, parameter);
                            } catch (e) {
                                // 実行時エラーなのでリトライ対象
                                await msSleep(sleep_ms);
                                sleep_ms = sleep_ms * 2; // 再送のたびに再送間隔は２倍になる
                                continue;
                            }
                            if (response.status === 200) {
                                var etag = response.headers.get('ETag');
                                etag = etag.replaceAll("\"", "");
                                partInfo.push({ ETag: etag, PartNumber: i + 1 });
                                isUploadDone = true;
                                break;
                            } else if (response.status === 401 || response.status === 403) {
                                // アクセスエラーは即時エラー
                                throw new Error("3");
                            } else {
                                await msSleep(sleep_ms);
                                sleep_ms = sleep_ms * 2; // 再送のたびに再送間隔は２倍になる
                            }
                        }
                        if (isUploadDone !== true) {
                            throw new Error("2");
                        }
                    }
                    // マルチパートの完了通知
                    ret = await getFileURL(docId, caseId, 4, uploadId, 0, partInfo);
                    if (ret === false) {
                        throw new Error("5");
                    }
                    upload_result[index] = {
                        result: true,
                        file: fileName.name,
                        size: fileName.size,
                        index: index,
                        error_code: "9"
                    };
                    uploadDoneDocIds.push(docId);
                    uploadDoneFileNames.push(fileName);
                    setUploadDoneDocIds(uploadDoneDocIds);
                    setUploadDoneFileNames(uploadDoneFileNames);
                    return resolve(1);
                }
            } catch (e) {
                let error;
                let error_code = e.message;
                let ec = parseInt(error_code, 10);
                if (error_message.length > ec) {
                    error = error_message[ec];
                } else {
                    error = error_message[error_message.length - 1];
                }
                upload_result[index] = {
                    result: false,
                    file: fileName.name,
                    size: fileName.size,
                    index: index,
                    error: error,
                    error_code: error_code
                };
                return resolve(0);
            }

        });
        let thread_num = fileNames.length;
        var successUploadNum = 0;
        for (let i = 0; i < Math.ceil(thread_num / MAX_UPLOAD_THREAD); i++) {
            let uploadThread = [];
            for (let j = MAX_UPLOAD_THREAD * i; j < Math.min(thread_num, (MAX_UPLOAD_THREAD * i) + MAX_UPLOAD_THREAD); j++) {
                uploadThread.push(uploadFileFuncs(fileNames[j], j));
            }
            let results = await Promise.all(uploadThread);
            for (let j = 0; j < results.length; j++) {
                if (results[j] === 1) {
                    successUploadNum++;
                }
            }
        }
        let deleteIndex = [];
        let reversedDeleteIndex;
        upload_result.forEach((el) => {
            if (el.result === true) {
                deleteIndex.push(el.index);
                setFileNamesState((prev) => [...prev, { name: el.file, size: el.size }]);
            }
        });

        if (deleteIndex) {
            reversedDeleteIndex = deleteIndex.reverse();
            for (let i = 0; i < reversedDeleteIndex.length; i++) {
                onRemoveClick(reversedDeleteIndex[i]);
            }
        }
        if (successUploadNum === fileNum) {
            var next_state = prev_state;
            next_state.fileNames = uploadDoneFileNames;
            let uploadDone = await indicateUploadDone(true);
            if (uploadDone === true) {
                initFiles();
                navigate("/file_upload_complete", { state: next_state });
            }
        }
        else {
            // 失敗がある
            let error_file = "";
            let error_code = "Error Code:";
            for (var i = 0; i < upload_result.length; i++) {
                if (!upload_result[i].result) {
                    error_file = error_file + " " + upload_result[i].file;
                }
                error_code = error_code + upload_result[i].error_code;
            }
            for (; i < MAX_FILE_NUM; i++) {
                error_code = error_code + "9";
            }
            alert(error_file + "のアップロードに失敗しました。\n"
                + "再度アップロードボタンをクリックしてください。\n"
                + error_code);
        }
        handleUploadLoading(false);
    }

    const doUploadCancel = () => {
        let ret = window.confirm("アップロードを中止してよろしいですか？")
        if (!ret) {
            return;
        }
        indicateUploadDone(false);
        initFiles();
        navigate("/inquiry_completion", { state: { page: true } })
    }

    const goNext = async () => {
        if (fileNum !== 0) {
            var ret = window.confirm("アップロード予定のファイルが残っています。すべてクリアして、アップロードを終了してもよろしいですか？")
            if (!ret) {
                return;
            }
        }
        var next_state = prev_state;
        var uploadDone = await indicateUploadDone(true);
        next_state.fileNames = fileNamesState;
        if (uploadDone) {
            initFiles();
            navigate("/file_upload_complete", { state: next_state });
        }
    }

    const fileValidator = (file) => {
        if (file.path) {
            if (file.name.length > MAX_FILENAME_LEN) {
                alert(`アップロードファイル名は${MAX_FILENAME_LEN}文字以内で入力してください。`)
                return {
                    code: "name-too-large",
                };
            }

            if (file.size > (FILE_MAX_SIZE_GB * 1024 * 1024 * 1024)) {
                alert(`アップロードできるファイルの最大サイズは50GBです。`);
                return false;
            }
        }

        return null;
    }
    const onDrop = useCallback((acceptedFiles, index) => {
        var sum = 0;
        fileNames.forEach((fileName) => {
            sum = sum + fileName.size;
        });

        if (acceptedFiles.length + fileNum > MAX_FILE_NUM) {
            alert(`一度にアップロードできるファイル数は、10ファイルまでです。`);
            return;
        }

        acceptedFiles.forEach((acceptedFile) => {
            if (((sum + acceptedFile.size) > (FILE_MAX_SUM_SIZE_GB * 1024 * 1024 * 1024))) {
                alert(`一度にアップロードできる最大容量は、全ファイルの合計が500GBまでです。`);
                return;
            }
        })

        acceptedFiles.forEach((acceptedFile) => {
            if (((sum + acceptedFile.size) <= (FILE_MAX_SUM_SIZE_GB * 1024 * 1024 * 1024)) && (fileNum < MAX_FILE_NUM)) {
                sum = sum + acceptedFile.size;
                let array;
                array = fileNames;
                array.push(acceptedFile);
                setFileNames(array);
                setFileNum(array.length);
            }

        });
    }, [fileNames, fileNum, setFileNames, setFileNum]);

    const {
        getRootProps,
        getInputProps,
        isFocused,
        isDragAccept,
        isDragReject,
    } = useDropzone({ onDrop, validator: fileValidator, maxSize: (FILE_MAX_SIZE_GB * 1024 * 1024 * 1024) });

    const style = useMemo(
        () => ({
            ...baseStyle,
            ...(isFocused ? focusedStyle : {}),
            ...(isDragAccept ? acceptStyle : {}),
            ...(isDragReject ? rejectStyle : {}),
        }),
        [isFocused, isDragAccept, isDragReject]
    );

    const onRemoveClick = useCallback((index) => {
        let array = fileNames;
        array.splice(index, 1);
        setFileNames(array);
        setFileNum(array.length);
    }, [fileNames, setFileNames, setFileNum]);

    const fileListUpload = fileNames.map((fileName, index) => {
        const i = index;
        return <FileListElement
            name={fileName.name}
            size={fileName.size}
            key={i}
            isRemoveButton={true}
            onRemoveClick={() => onRemoveClick(index)
            } />
    });

    const fileListSuccess = fileNamesState.map((fileName, index) => {
        const i = index;
        return <FileListElement
            name={fileName.name}
            size={fileName.size}
            key={i}
            isRemoveButton={false}
            onRemoveClick={() => onRemoveClick(index)
            } />
    });

    return (
        <div className="ls-row str-column-main" id="ls-row-3-col-1-row-2-col-2-row-1">
            <div className="str-outer">
                <div className="str-inner">
                    <div className="mod-hdg-l1">
                        <div className="inner">
                            <h1 className="hdg">Web問い合わせシステム</h1>
                            <span className="sub of-top">NEC ファーストコンタクトセンター</span>
                        </div>
                    </div>
                    <h2 className="mod-hdg-l2-02">
                        <span>添付ファイル アップロード</span>
                    </h2>

                    <div className="mod-txt">
                        <p>
                            資料をアップロードしてください。
                        </p>
                    </div>
                    <h4 className="mod-hdg-l4-02">
                        資料アップロード先
                    </h4>
                    <div className="mod-txt">
                        <ul>
                            <li>受付番号：<span id="receipt_no">{caseId}</span></li>
                        </ul>
                    </div>


                    <h4 className="mod-hdg-l4-02">
                        アップロード資料
                    </h4>

                    <div>
                        <div {...getRootProps({ style })}>{ }
                            <input {...getInputProps()} />
                            <p>
                                アップロードする資料をドラッグするかクリックしてファイルを選択してください。<br />
                                選択したファイルを取り消す場合は、[クリア]ボタンを押してください。
                            </p>
                            <p>
                                最大{MAX_FILE_NUM}ファイルまで選択できます。
                            </p>
                        </div>
                    </div>


                    <div>
                        <div className="mod-txt">
                            <p>
                                1ファイルの最大容量は{FILE_MAX_SIZE_GB}Gバイトまでで、最大{MAX_FILE_NUM}ファイルまでアップロードできます。
                            </p>
                        </div>
                        <div className="mod-txt">
                            <p>
                                コンピューターウィルスが含まれたファイルがアップロードされた場合、自動的に消去されるため、アップロードすることができません。
                            </p>
                        </div>
                        <div className="mod-txt">
                            <p>
                                ファイルのアップロード処理にかかる時間の目安は1GByteで約3～4分(※)です。<br />
                                (※)この値は、当システムを他に利用している方がいない場合の概算値です。<br />
                                当システムの負荷状況、経由するインターネット回線の負荷状況により、上記より大幅に時間がかかる場合があります。
                            </p>
                        </div>
                    </div>
                    <div>アップロード予定：{fileNum}</div><br />
                    <div>
                        {fileListUpload}
                    </div>
                    <div>アップロード済み：{fileNamesState.length}</div><br />
                    <div>
                        {fileListSuccess}
                    </div>
                    <div className="mod-btn of-list">
                        {fileNamesState.length === 0 ? <div className="btn">
                            <button onClick={doUploadCancel} className="of-left">アップロード中止</button>
                        </div> : null}
                        <div className="btn">
                            <button type="button" id="btn-send" className="btn_default_select"
                                onClick={upload}>アップロード</button>
                        </div>
                        <div className="btn">
                            <button type="button" disabled={fileNamesState.length !== 0 ? false : true} className="of-right" style={{ padding: "1px 50px 1px 34px" }}
                                onClick={goNext}>アップロード終了</button>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    )
}
export default MainComponent;